/**
 * CSV exporter per M3X.REPORTS §0.2.
 *
 * - UTF-8 with BOM prefix (`﻿`) so Excel on Windows decodes
 *   Arabic column headers correctly.
 * - RFC 4180 quoting (commas / quotes / newlines escape as
 *   `"…""…"`).
 * - Dates serialize as ISO-8601 (YYYY-MM-DD) for unambiguous
 *   downstream parsing.
 * - Numbers use the JS default locale-free `String(n)` — locale
 *   formatting is reserved for the rendered table + the XLSX path.
 * - Brand header omitted in CSV (CSV is for machines; the brand
 *   header would break round-trip imports).
 */

import type { ExportColumn, ExportPayload } from "./types";

/**
 * Build the CSV body string.
 *
 * Accepts EITHER an `ExportPayload` (the M3X.REPORTS §0.2 shape:
 * columns + rows + display labels) OR a legacy `rows[]` array
 * where each row is `{ headerLabel: value }`. The two shapes are
 * disambiguated by structure inspection.
 */
export function buildCsvString(
  arg: ExportPayload | Array<Record<string, unknown>>,
): string {
  if (Array.isArray(arg)) {
    return _buildCsvFromLegacyRows(arg);
  }
  const { columns, rows } = arg;
  const lines: string[] = [];
  lines.push(columns.map((c) => csvEscape(c.label)).join(","));
  for (const row of rows) {
    lines.push(
      columns
        .map((c) => csvEscape(stringifyForCsv(row[c.key], c)))
        .join(","),
    );
  }
  return lines.join("\n");
}

function _buildCsvFromLegacyRows(
  rows: Array<Record<string, unknown>>,
): string {
  if (rows.length === 0) return "";
  const headers = Object.keys(rows[0]);
  const lines: string[] = [headers.join(",")];
  for (const row of rows) {
    lines.push(headers.map((h) => csvEscape(_legacyCellToString(row[h]))).join(","));
  }
  return lines.join("\n");
}

function _legacyCellToString(v: unknown): string {
  if (v === null || v === undefined) return "";
  return String(v);
}

export async function exportCsv(
  payload: ExportPayload,
  filename: string,
): Promise<void> {
  // BOM prefix for Excel-on-Windows Arabic decoding.
  const text = "﻿" + buildCsvString(payload);
  triggerDownload(
    new Blob([text], { type: "text/csv;charset=utf-8" }),
    filename,
  );
}

function csvEscape(value: string): string {
  if (value === null || value === undefined) return "";
  if (value.includes('"') || value.includes(",") || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function stringifyForCsv(value: unknown, col: ExportColumn): string {
  if (value === null || value === undefined) return "";
  switch (col.type) {
    case "date":
      if (value instanceof Date) {
        return value.toISOString().slice(0, 10);
      }
      return String(value);
    case "number":
    case "currency": {
      const n = typeof value === "number" ? value : Number(value);
      return Number.isNaN(n) ? String(value) : String(n);
    }
    case "boolean":
      return value ? "true" : "false";
    case "string":
    default:
      return String(value);
  }
}

function triggerDownload(blob: Blob, filename: string): void {
  if (typeof window === "undefined") return;
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
